<mat-expansion-panel [expanded]="initialExpanded" (opened)="toggleExpanded()" (closed)="toggleExpanded()">
    <mat-expansion-panel-header>
        <mat-panel-title>DOCUMENTS</mat-panel-title>
        <mat-panel-description class="documents-header-actions">
            <button mat-icon-button type="button" *ngIf="!stateMode" (click)="openSendDialog($event)"
                matTooltip="Send a document to this patient">
                <mat-icon>send</mat-icon>
            </button>
        </mat-panel-description>
    </mat-expansion-panel-header>

    <div class="documents-info">
        <ng-container *ngIf="requests; else listLoading">
            <ng-container *ngIf="requests.length; else noResults">
                <mat-list class="full-width">
                    <ng-container *ngFor="let request of requests; trackBy: trackByRequest">
                        <mat-list-item class="auto-height normal-spacing">
                            <!--
                                ONE STACKED COLUMN, which is the house shape for a list row.

                                `patient-clinical-timeline` line 102, `patient-invoicing-list`,
                                `patient-survey-responses` and `prescription-list` all draw a row as
                                <item> > <details> with the facts stacked and `.list-actions icons`
                                as a full-width bar at the BOTTOM. This panel was the only one that
                                made it a two-column flex with the actions beside the facts, and
                                that is what broke it - see the note above `.document-item` in the
                                stylesheet.

                                `.list-actions` stays a SIBLING of `.details` rather than moving
                                inside it, which is the one place this differs from the timeline's
                                markup. `.details` carries the row's `(click)` toggle. The three
                                BUTTONS in the action bar all `stopPropagation`, but the two bare
                                `<mat-icon>` explainers next to them do not and cannot - they have
                                no handler - so inside `.details` a click on a tooltip icon would
                                expand the row. A sibling renders identically and keeps the
                                toggle's boundary where it was.
                            -->
                            <div class="document-item">
                                <div class="details" [class.openable]="canOpen(request)"
                                    (click)="toggleRow(request)">

                                    <div matLine class="document-title">
                                        <!--
                                            THE RECORD-TYPE ICON, the clinical timeline's own
                                            idiom (`.record-type-icon`, one per source, with a
                                            tooltip naming the source). This panel is the fourth
                                            source's home screen and had none, so a row here read
                                            unlike the same document's row on the timeline. It
                                            carries the REVIEW state's colour rather than a single
                                            flat one, because on this screen "somebody still has
                                            to look at this" is the fact worth seeing first.
                                        -->
                                        <mat-icon class="record-type-icon"
                                            [ngClass]="reviewIconClass(request)"
                                            [matTooltip]="documentIconTooltip(request)"
                                            data-test="document-icon">description</mat-icon>
                                        <span>{{ documentName(request) }}</span>
                                        <!--
                                            Six statuses, six looks. `Not sent yet` is called out
                                            separately from `Awaiting patient` because a scheduled
                                            ask nobody has been given must never read like one the
                                            patient has ignored (practice-documents.md 3.3).
                                        -->
                                        <span class="document-status" [ngClass]="statusClass(request)"
                                            data-test="document-status">
                                            {{ statusLabel(request) }}
                                        </span>
                                        <!--
                                            THE REVIEW STATE. 0 decision 3: a person checks every
                                            return, so a returned document that nobody has looked at
                                            says so on the row rather than only inside it.
                                        -->
                                        <span class="document-review" *ngIf="hasReview(request)"
                                            [ngClass]="reviewClass(request)" data-test="review-status">
                                            {{ reviewLabel(request) }}
                                        </span>
                                    </div>

                                    <div matLine class="document-dates">
                                        <span *ngIf="sentAt(request)">
                                            Sent {{ sentAt(request) | date: 'longDate' }}
                                        </span>
                                        <span *ngIf="request.returnedAt">
                                            Returned {{ request.returnedAt | date: 'longDate' }}
                                        </span>
                                        <span *ngIf="fileCountLabel(request)">
                                            {{ fileCountLabel(request) }} held
                                        </span>
                                        <!--
                                            `Resent N times` USED TO BE HERE and is gone (21.1).
                                            It counted `PatientDocumentRequest.resendCount`, which
                                            the per-document resend maintained. Nothing writes that
                                            column now - a resend increments the INVITE'S counter -
                                            so the number would be frozen wherever the old rail
                                            left it, and 0 forever on every row raised since. A
                                            counter that has stopped counting is worse than none.
                                        -->
                                    </div>

                                    <!-- The blank AS SENT (11). Omitted, never guessed, when the
                                         pin is NULL - which is ordinary on an older or unsent row. -->
                                    <div matLine class="document-sent-file" *ngIf="sentFileName(request)">
                                        <mat-icon>attach_file</mat-icon>
                                        <span>Sent as {{ sentFileName(request) }}</span>
                                    </div>

                                    <!--
                                        WHY IT WAS NEVER SENT, AND WHAT TO DO ABOUT IT (25.4).

                                        The three reasons have three different remedies - send it
                                        by hand, change the appointment type, edit the patient - so
                                        a chip saying only "Not sent" leaves the practice guessing
                                        which. Both sentences are on the row rather than behind an
                                        expand, because this is the row they came here to act on.
                                    -->
                                    <div matLine class="document-skip" *ngIf="skipDescription(request) as skip"
                                        [ngClass]="'skip-' + (skip.reason || 'unknown')"
                                        data-test="document-skip">
                                        <mat-icon>report_problem</mat-icon>
                                        <span class="document-skip-why" data-test="document-skip-why">{{ skip.why }}</span>
                                        <span class="document-skip-remedy" data-test="document-skip-remedy">{{ skip.remedy }}</span>
                                    </div>

                                    <div matLine class="document-open-hint" *ngIf="canOpen(request)">
                                        <mat-icon>{{ isOpen(request) ? 'expand_less' : 'expand_more' }}</mat-icon>
                                        <span>{{ openHint(request) }}</span>
                                    </div>
                                </div>

                                <!--
                                    `list-actions icons`, BARE, exactly as the timeline and every
                                    sibling list write it. The `action-icons` that used to be here
                                    as well is gone: it set `flex: 0 0 auto` to make this a column
                                    beside the facts, and in doing so left the global rule's
                                    `width: 100%` in force inside a flex ROW - so the action bar
                                    claimed the whole row and the facts collapsed to one word per
                                    line. In a column that class has nothing left to say.
                                -->
                                <div class="list-actions icons">
                                    <!--
                                        AN INVITE RESEND (21.1). Nothing rotates: the patient is
                                        sent the SAME link again, and whatever is outstanding for
                                        them is picked up fresh when they open it. Disabled while
                                        the INVITE'S cooldown runs - shared by every document that
                                        rode the same message.
                                    -->
                                    <button mat-icon-button type="button" *ngIf="canResend(request)"
                                        [disabled]="!resendAllowed(request) || busyRequestId === request.id"
                                        [matTooltip]="resendTooltip(request)"
                                        data-test="document-resend"
                                        (click)="resend(request, $event)">
                                        <mat-icon>send</mat-icon>
                                    </button>

                                    <!--
                                        SEND IT NOW (23.1). A DIFFERENT action from a resend:
                                        there is no invite to resend, because nothing was ever
                                        sent. The endpoint re-schedules the row to now and
                                        delivers inside the request, so the button does what it
                                        says on the day the practice presses it.
                                    -->
                                    <button mat-icon-button type="button" *ngIf="canSendNow(request)"
                                        [disabled]="busyRequestId === request.id"
                                        matTooltip="Send this document to the patient now"
                                        data-test="document-send-now"
                                        (click)="sendNow(request, $event)">
                                        <mat-icon>schedule_send</mat-icon>
                                    </button>

                                    <!--
                                        I ALREADY HAVE THIS DOCUMENT - `tasks/document-rejection-resend.md` 8.

                                        The patient emailed the form back, walked in with a printed
                                        copy, or is never going to use the link. Without this the
                                        only options were to leave the ask open for ever or to
                                        REVOKE it - which records that the practice withdrew the
                                        ask, not that it was answered.

                                        It files the same return the patient's own upload files, so
                                        the row starts 26's review at `awaiting_review` like any
                                        other. Nothing skips a step.
                                    -->
                                    <button mat-icon-button type="button" *ngIf="canFileReturn(request)"
                                        [disabled]="busyRequestId === request.id"
                                        matTooltip="I have this document - file it against this request"
                                        data-test="document-file-return"
                                        (click)="openFileReturn(request, $event)">
                                        <mat-icon>upload_file</mat-icon>
                                    </button>

                                    <!--
                                        An UNREACHABLE patient gets no Send-now button, on purpose:
                                        with no email address and no mobile number the send would
                                        mark the row sent and deliver nowhere. The remedy is shown
                                        instead of a control that cannot work.
                                    -->
                                    <mat-icon class="document-no-send-now"
                                        *ngIf="sendNowUnavailableReason(request)"
                                        data-test="document-no-send-now"
                                        [matTooltip]="sendNowUnavailableReason(request)">
                                        person_off
                                    </mat-icon>

                                    <!--
                                        A row raised before documents moved onto the invite rail has
                                        no invite and no link anyone can send. Saying so beats an
                                        absent button, which teaches a receptionist nothing.
                                    -->
                                    <mat-icon class="document-no-resend"
                                        *ngIf="resendUnavailableReason(request)"
                                        data-test="document-no-resend"
                                        [matTooltip]="resendUnavailableReason(request)">
                                        info_outline
                                    </mat-icon>

                                    <button mat-icon-button type="button" *ngIf="canRevoke(request)"
                                        [disabled]="busyRequestId === request.id"
                                        matTooltip="Cancel this document request"
                                        (click)="revoke(request, $event)">
                                        <mat-icon>block</mat-icon>
                                    </button>
                                </div>
                            </div>
                        </mat-list-item>

                        <!--
                            WHAT CAME BACK, AND THE DECISION SOMEBODY HAS TO TAKE ABOUT IT.

                            A child table, not a column (3.4): the realistic return is a phone
                            photograph per page, so this is always a list.
                        -->
                        <div class="document-return" *ngIf="isOpen(request)" data-test="document-return">
                            <div class="document-return-loading" *ngIf="detailLoading[request.id]">
                                <mat-spinner color="primary" mode="indeterminate" diameter="20"></mat-spinner>
                            </div>

                            <p class="document-return-error" *ngIf="detailError[request.id]">
                                {{ detailError[request.id] }}
                            </p>

                            <ng-container *ngIf="details[request.id]">
                                <!--
                                    WHAT CAME BACK, AS A GALLERY OF PAGES.

                                    THE SAME IDIOM THE CLINICAL TIMELINE USES for note media -
                                    `patient-clinical-timeline.component.html` line 147 -
                                    `<app-file-gallery>` fed by `mapMediaToFileObject`. A returned
                                    document is a stack of pages and a reviewer's job (0 decision
                                    3) is to LOOK at them; a list of filenames made them click one
                                    link per page into a new tab and lose their place.

                                    `[deleteEnabled]="false"` - a reviewer must not be able to
                                    delete a patient's returned pages from a review screen. The
                                    only thing that removes a page here is a REJECTION, which
                                    deactivates the whole generation and keeps it (13.2).

                                    `[enableEditing]="false"` IS A DELIBERATE DEPARTURE from the
                                    timeline's `true`, and it is the one thing on this screen that
                                    does not copy that line. `ImageDialogComponent`'s editing saves
                                    through `PatientNoteService.uploadAnnotatedMedia`, which posts
                                    `originalMediaId` + `patientNoteId` to
                                    `POST /notes/media/annotated` and writes a `PatientNoteMedia`
                                    row into `patient-<id>-media`. A `PatientDocumentRequestMedia`
                                    id means nothing there: the annotation would land in the wrong
                                    table and the wrong container, never appear on this panel, and
                                    report success - lessons.md 18 exactly. Turning it on needs a
                                    document-side annotated-media endpoint first.

                                    THERE IS NO `[ignoreSorting]` BINDING, and its absence is the
                                    considered answer rather than an omission. That input is only
                                    consulted inside `FileGalleryComponent.ngOnChanges`, on the
                                    branch that re-maps RAW MEDIA ROWS - and `galleryFor` hands
                                    this gallery `FileObject`s that `mapMediaToFileObject` has
                                    already sorted by `displayIndex`, which IS page order (3.4).
                                    Setting it would be a binding nothing reads, which is the
                                    defect this feature removed `startedAt` for.
                                -->
                                <div class="document-files" *ngIf="filesFor(request).length; else noFiles">
                                    <div class="document-files-label">
                                        {{ filesFor(request).length }} file{{ filesFor(request).length === 1 ? '' : 's' }}
                                        came back from the patient
                                    </div>

                                    <app-file-gallery *ngIf="galleryFor(request).length"
                                        data-test="returned-gallery"
                                        [fileObjects]="galleryFor(request)"
                                        [deleteEnabled]="false"
                                        [enableEditing]="false"></app-file-gallery>

                                    <!--
                                        WHAT THE GALLERY CANNOT DRAW, and it must still be
                                        reachable.

                                        Two kinds of row end up here. A PDF, because
                                        `mapMediaToFileObject` filters PDFs out of a gallery - and
                                        since the return path converts, a PDF row means that
                                        CONVERSION FAILED (utils/patientDocumentRequestUtils.js
                                        `convertReturnedPdfToPages`). That return is filed and
                                        complete; the reviewer simply opens the document instead of
                                        scrolling its pages. And a row whose blob reference is
                                        unusable, which comes back with `url: null` on purpose.

                                        Losing either would leave a reviewer looking at a shorter
                                        document than the patient sent, with nothing saying so.
                                    -->
                                    <div class="document-files-fallback"
                                        *ngIf="fallbackFilesFor(request).length"
                                        data-test="fallback-files">
                                        <ng-container *ngFor="let media of fallbackFilesFor(request); let i = index; trackBy: trackByMedia">
                                            <!-- `url` is minted per request and expires; nothing
                                                 caches it, which is why the row is re-read to open
                                                 a file. -->
                                            <a class="document-file" *ngIf="canOpenFile(media)"
                                                [attr.href]="media.url" target="_blank" rel="noopener"
                                                data-test="returned-file">
                                                <mat-icon>{{ fileIcon(media) }}</mat-icon>
                                                <span>{{ fileName(media, i) }}</span>
                                            </a>
                                            <!-- A file whose blob reference is unusable renders,
                                                 because a reviewer has to learn the page exists and
                                                 cannot be opened rather than silently see one
                                                 fewer page. -->
                                            <div class="document-file document-file--broken"
                                                *ngIf="!canOpenFile(media)" data-test="unopenable-file">
                                                <mat-icon>report_problem</mat-icon>
                                                <span>{{ fileName(media, i) }} - this file cannot be opened</span>
                                            </div>
                                        </ng-container>
                                    </div>
                                </div>

                                <!--
                                    WHAT A REJECTION REPLACED. Kept, not deleted, and shown apart:
                                    they are the evidence the rejection note was written about, and
                                    mixing them into the live list would show a reviewer pages the
                                    patient has already replaced.

                                    STILL A LIST, NOT A SECOND GALLERY, and that is deliberate. The
                                    demotion - struck through, faded, labelled - is what stops a
                                    reviewer mistaking a replaced page for a current one, and two
                                    galleries one above the other would read as one document of
                                    twice the length.
                                -->
                                <div class="document-files document-files--superseded"
                                    *ngIf="supersededFilesFor(request).length"
                                    data-test="superseded-files">
                                    <div class="document-files-label">
                                        Replaced after the rejection - kept for reference
                                    </div>
                                    <a class="document-file" *ngFor="let media of supersededFilesFor(request); let i = index; trackBy: trackByMedia"
                                        [attr.href]="media.url" target="_blank" rel="noopener"
                                        data-test="superseded-file">
                                        <mat-icon>{{ fileIcon(media) }}</mat-icon>
                                        <span>{{ fileName(media, i) }}</span>
                                    </a>
                                </div>

                                <!--
                                    THE "AI SUMMARY" PANEL WAS HERE and the whole of it is gone -
                                    9 Sep 2026, owner's call.

                                    It drew the summary text, the score chip, the machine-read
                                    caveat and the empty state that told a reviewer no read had
                                    landed yet. THE READ ITSELF IS UNCHANGED: the
                                    column, the worker, the enqueue and the scoring all still run,
                                    and the summary is still served by
                                    `GET /patient-document-requests/:id`. It feeds Cura, which
                                    loads its own context when it needs it, and does not need to be
                                    shown to a reviewer.

                                    Removed with it: the `patientDocumentAiRead` socket event
                                    (server emit, service listener and this panel's detail-cache
                                    drop) and `aiSummary` on the client DTO - an event nobody
                                    listens to and a field nothing renders are the same defect this
                                    feature removed `startedAt` for. `hasAiSummary` and the two
                                    score columns STAY on the DTO because the CLINICAL TIMELINE
                                    still draws both.

                                    `patient-document-panel.spec.ts` "the AI read" block is the
                                    INVERTED assertion (lessons.md 23): it proved the summary
                                    rendered, and now proves no AI text reaches this screen at all,
                                    so the block cannot creep back unnoticed.
                                -->

                                <!--
                                    THE REVIEW - §0 decision 3. There is no automated check that this is the right document
                                    and actually filled in, so a person does it.
                                
                                    IT OPENS A DIALOG NOW. The two buttons and the reject-note box used to be drawn inline,
                                    right here, and they moved to `shared/components/document-review-dialog` so that the
                                    Inbox (`patients/inbox`) could offer the same decision without a second implementation
                                    of it - `tasks/inbox.md` §11. Stage two below was already a dialog; the two now behave
                                    identically, which they did not before.
                                -->
                                <div class="document-review-actions" *ngIf="canReview(request)" data-test="review-actions">
                                    <p class="document-review-lead">
                                        Check that this is the right document and that it has been filled in. Nothing else
                                        verifies it. Signing it off does <strong>not</strong> put it on the patient's
                                        timeline &mdash; a clinician validates it next.
                                    </p>
                                    <button mat-flat-button color="primary" type="button"
                                        [disabled]="busyRequestId === request.id"
                                        (click)="openReview(request, $event)" data-test="open-review">
                                        <mat-icon>fact_check</mat-icon>
                                        Review this document
                                    </button>
                                </div>

                                <!--
                                    §26 STAGE TWO — the clinical step.

                                    Offered ONLY on a document somebody has already signed off,
                                    which is the workflow made visible: there is no Validate button
                                    on a document nobody has checked is the right one. The server
                                    refuses it as well (`409 NOT_SIGNED_OFF`); this only keeps a
                                    button off the screen that would be refused if pressed.

                                    Both stage-two decisions live in the dialog rather than out
                                    here, because they are one moment for the person taking them:
                                    they are reading the document, and they either put it on the
                                    record or send it back.
                                -->
                                <div class="document-validate-actions" *ngIf="canValidate(request)"
                                    data-test="validate-actions">
                                    <p class="document-review-lead">
                                        This has been signed off as the right document. Read it,
                                        record what you observed, and it goes on the patient's
                                        clinical timeline.
                                    </p>
                                    <button mat-flat-button color="primary" type="button"
                                        [disabled]="busyRequestId === request.id"
                                        (click)="openReview(request, $event)"
                                        data-test="open-validate">
                                        <mat-icon>library_add</mat-icon>
                                        Validate to timeline
                                    </button>
                                </div>

                                <!--
                                    §26 — WHAT THE CLINICIAN SAW, and what they attached.

                                    Drawn from the DETAIL copy: the observations are encrypted and
                                    the list endpoint deliberately does not serve them, so this
                                    only has anything to say once the row is expanded.
                                -->
                                <div class="document-validation" *ngIf="isValidated(request)"
                                    data-test="validation-outcome">
                                    <div class="document-validation-head">
                                        <mat-icon>verified</mat-icon>
                                        <span data-test="validated-by">
                                            Validated<ng-container *ngIf="validatedByFor(request)">
                                                by {{ validatedByFor(request) }}</ng-container
                                            ><ng-container *ngIf="validatedAtFor(request)">
                                                on {{ validatedAtFor(request) | date: 'longDate' }}</ng-container>
                                        </span>
                                    </div>

                                    <p class="document-validation-note"
                                        *ngIf="validationNoteFor(request)"
                                        data-test="validation-note">
                                        {{ validationNoteFor(request) }}
                                    </p>

                                    <!--
                                        A SEPARATELY LABELLED gallery, never mixed with the
                                        returned pages above it. What the patient sent and what the
                                        practice added are two provenances, and on a clinical
                                        record the difference is part of the content.
                                    -->
                                    <ng-container *ngIf="validationGalleryFor(request).length">
                                        <div class="document-files-label">
                                            Added on review
                                        </div>
                                        <app-file-gallery data-test="validation-gallery"
                                            [fileObjects]="validationGalleryFor(request)"
                                            [deleteEnabled]="false"
                                            [enableEditing]="false"></app-file-gallery>
                                    </ng-container>
                                </div>

                                <!-- The decision, once taken. -->
                                <!--
                                    WHERE THE PAGES CAME FROM, when it was not the patient.

                                    A reviewer must be able to tell: a document somebody in the
                                    practice typed in on a patient's behalf is a different fact from
                                    one the patient sent. Drawn ONLY for a practice-filed return -
                                    a patient return is the ordinary case and needs no label.
                                -->
                                <div class="document-filed-by" *ngIf="request.returnSource === 'practice'"
                                    data-test="document-filed-by">
                                    <mat-icon>upload_file</mat-icon>
                                    <span>
                                        Filed by the practice<span *ngIf="request.returnedByName">,
                                            {{ request.returnedByName }}</span> - the patient did not
                                        upload this.
                                    </span>
                                </div>

                                <div class="document-review-outcome" *ngIf="!canReview(request) && hasReview(request)"
                                    data-test="review-outcome">
                                    <span class="document-review" [ngClass]="reviewClass(request)">
                                        {{ reviewLabel(request) }}
                                    </span>
                                    <span class="document-review-when" *ngIf="reviewedAtFor(request)">
                                        {{ reviewedAtFor(request) | date: 'longDate' }}
                                    </span>
                                    <p class="document-review-note" *ngIf="reviewNoteFor(request)"
                                        data-test="review-note">
                                        {{ reviewNoteFor(request) }}
                                    </p>
                                </div>
                            </ng-container>
                        </div>

                    </ng-container>
                </mat-list>
            </ng-container>
        </ng-container>
    </div>
</mat-expansion-panel>

<ng-template #listLoading>
    <div class="list-loading">
        <mat-spinner color="primary" mode="indeterminate" diameter="20" style="float:right;"></mat-spinner>
    </div>
</ng-template>

<ng-template #noResults>
    <p class="no-records-message">No documents have been sent to this patient yet.</p>
</ng-template>

<!--
    A returned request with nothing live attached is a real state and reads as a problem, not as an
    empty list: the patient was told their upload landed, so somebody has to notice.

    It is ALSO the ordinary state of a rejected-and-re-asked row, whose files were deactivated when
    the rejection re-opened it - so the wording has to cover both without alarming anybody about
    the second. The superseded list below it says what happened.
-->
<ng-template #noFiles>
    <p class="document-return-error">
        There are no current files on this request.
    </p>
</ng-template>

<!--
    §26 STAGE TWO's DIALOG WAS HERE, and it is gone - `tasks/inbox.md` §11.

    It was an `ng-template` opened through `MatDialog` against a `validating` scratch object on
    this component. Both review stages now live in `shared/components/document-review-dialog`,
    which the Inbox opens too, so that the rule deciding what goes on a patient's clinical record
    has ONE implementation rather than one per screen.

    `openReview(request)` is what both buttons above call.
-->

<!--
    "I ALREADY HAVE THIS DOCUMENT" - tasks/document-rejection-resend.md 8.

    An `ng-template` opened through `MatDialog` against the component's `filing` scratch object -
    the idiom the 26 validate dialog used before it moved to a shared component, and
    `patient-ai-request-detail` before that. Template-driven rather than reactive for the same
    reason: the object is rebuilt wholesale every time the dialog opens, and a `FormGroup` would
    have to be rebuilt with it.

    WHY THIS IS NOT THE SHARED REVIEW DIALOG. That dialog is for JUDGING pages that have arrived.
    This is how they arrive. Merging them would put an upload box on a screen whose whole job is to
    show a reviewer what is already there.
-->
<ng-template #fileReturnDialog>
    <h1 mat-dialog-title>File this document</h1>

    <div mat-dialog-content>
        <p class="document-filing-lead" *ngIf="filing.request">
            <strong>{{ documentName(filing.request) }}</strong> - attach the completed pages you
            have. This closes the request as if the patient had sent them, and it still has to be
            signed off afterwards.
        </p>
        <p class="document-filing-note">
            It will be recorded as filed by the practice rather than by the patient, under your
            name.
        </p>

        <!--
            `[pasteEnabled]="false"` is MANDATORY inside a dialog: the component registers a
            DOCUMENT-level paste listener. There is no textarea here for it to steal from today,
            and there is no reason to leave a document-level listener armed inside a modal.

            `showGallery` at its default true and no `shouldRefresh`, so the pages render as a
            gallery - reception scanning five pages wants to see five pages, not five filenames.
        -->
        <app-file-upload class="full-width document-filing-upload" [fileObjects]="[]"
            [allowMultiple]="true" [allowedFileTypes]="'image/*, application/pdf'"
            [pasteEnabled]="false" [cameraEnabled]="true" [enableEditing]="true"
            title="Attach the completed document"
            (fileUpload)="onFilingPicked($event)"
            (fileDelete)="onFilingDeleted($event)"
            (filesEdited)="onFilingEdited($event)"
            hintMessage="Drag the scan here, photograph each page, or click to choose">
        </app-file-upload>

        <p class="document-filing-error" *ngIf="filing.error" role="alert"
            data-test="document-filing-error">
            {{ filing.error }}
        </p>
    </div>

    <div mat-dialog-actions class="dialog-actions">
        <button mat-button type="button" (click)="closeFileReturn()"
            [disabled]="filing.sending">Cancel</button>
        <button mat-flat-button color="primary" type="button" (click)="submitFileReturn()"
            [disabled]="filing.sending" data-test="document-filing-submit">
            {{ filing.sending ? 'Filing...' : 'File this document' }}
        </button>
    </div>
</ng-template>
